import { Middleware, Dispatch } from 'redux';
import { getType } from 'typesafe-actions';
import { StorageFavoritesPath, AssetSymbolInfo, AssetType, SymbolType, UserProfileLevel } from '@tradingblock/types';
import { RootAction, FavoriteActions } from '../../actions';
import { DataState } from '../../state';
import { getFavorites } from '../../dataSelectors';
import { UiApiClient } from '../../../../context/Storage';
import _ from 'lodash';
import dayjs from 'dayjs';

const DEFAULT_VIRTUAL_FAVORITES: AssetSymbolInfo[] = [
  { symbol: 'SPY', type: 'stock' as SymbolType, assetType: AssetType.Equity, rootSymbol: 'SPY' },
  { symbol: 'QQQ', type: 'stock' as SymbolType, assetType: AssetType.Equity, rootSymbol: 'QQQ' },
  { symbol: 'IWM', type: 'stock' as SymbolType, assetType: AssetType.Equity, rootSymbol: 'IWM' },
];

const getFavoritesPath = (state: DataState): string => {
  const linkedAccount = state.linkedAccounts
    && state.linkedAccounts.accounts
    && state.linkedAccounts.accounts.find(a => a.accountId === state.account.accountId);
  const linkedUserName = linkedAccount && linkedAccount.userName;
  return linkedUserName ? `${StorageFavoritesPath}/${linkedUserName}` : StorageFavoritesPath;
};

const canModifyFavorites = (state: DataState): boolean => {
  const linkedAccounts = state.linkedAccounts && state.linkedAccounts.accounts;
  if (!linkedAccounts || linkedAccounts.length <= 1) {
    return true;
  }
  const currentAccountId = state.account.accountId;
  const userLevel = state.account.profile && state.account.profile.current && state.account.profile.current.level;
  const isOnLinkedAccount = linkedAccounts.some(a => a.accountId === currentAccountId);

  if (!isOnLinkedAccount) {
    return true;
  }

  if (userLevel === UserProfileLevel.Account) {
    console.warn('[Favorites] Account-level user cannot modify linked account favorites');
    return false;
  }

  return true;
};

export const loadFavorites = async (state: DataState, dispatch: Dispatch<RootAction>, linkedUserName?: string) => {
  const provider = UiApiClient(state, dispatch);
  if (provider) {
    try {
      const favPath = linkedUserName ? `${StorageFavoritesPath}/${linkedUserName}` : StorageFavoritesPath;
      const res = await provider.get<{ data: AssetSymbolInfo[] }>(favPath);
      let favoriteValue = res && res.data ? res.data : [];

      if (state.environment.isVirtual && _.isEmpty(favoriteValue)) {
        favoriteValue = DEFAULT_VIRTUAL_FAVORITES;
        await provider.save(favPath, favoriteValue);
      }

      const filteredFavoriteValue = favoriteValue.filter(symbol => {
        if (symbol.symbol.includes(' ')) {
          const [, contractInfo] = symbol.symbol.split(' ');
          const [year, month, day] = [contractInfo.substr(0, 2), contractInfo.substr(2, 2), contractInfo.substr(4, 2)];
          const expiry = new Date(`${month}/${day}/${year}`);
          return dayjs().isBefore(dayjs(expiry).add(1, 'day'));
        }
        return true;
      });

      dispatch(FavoriteActions.receive({ value: filteredFavoriteValue }));
    } catch (err) {
      console.error('[Favorites] Error loading favorites:', err);
    }
  }
};

const PERSIST_RETRY_COUNT = 10;
const PERSIST_RETRY_BASE_DELAY_MS = 1000;
const PERSIST_DEBOUNCE_MS = 400;

export const handlePersistFavorites = async (
  state: DataState,
  dispatch: Dispatch<RootAction>,
  updatedFavorites: AssetSymbolInfo[]
) => {
  const provider = UiApiClient(state, dispatch);
  if (!provider) return;

  const favPath = getFavoritesPath(state);
  let lastErr: unknown;

  for (let attempt = 1; attempt <= PERSIST_RETRY_COUNT; attempt++) {
    try {
      await provider.save(favPath, updatedFavorites);
      return;
    } catch (err) {
      lastErr = err;
      console.warn(`[Favorites] Persist attempt ${attempt}/${PERSIST_RETRY_COUNT} failed:`, err);
      if (attempt < PERSIST_RETRY_COUNT) {
        const delayMs = PERSIST_RETRY_BASE_DELAY_MS * attempt;
        await new Promise(resolve => setTimeout(resolve, delayMs));
      }
    }
  }

  console.error('[Favorites] Persist failed after', PERSIST_RETRY_COUNT, 'retries, giving up:', lastErr);
};

export const FavoriteMiddleware: Middleware<Dispatch<RootAction>, DataState, Dispatch<RootAction>> = ({
  dispatch,
  getState,
}) => {
  let persistTimerId: ReturnType<typeof setTimeout> | null = null;

  return (next: Dispatch<RootAction>) => (action: RootAction) => {
    try {
      const prevState = getState();
      const result = next(action);
      const nextState = getState();
      switch (action.type) {
        case getType(FavoriteActions.addFavorite):
        case getType(FavoriteActions.remove): {
          if (!canModifyFavorites(prevState)) {
            break;
          }
          if (persistTimerId) {
            clearTimeout(persistTimerId);
          }
          persistTimerId = setTimeout(() => {
            persistTimerId = null;
            const state = getState();
            handlePersistFavorites(state, dispatch, getFavorites(state));
          }, PERSIST_DEBOUNCE_MS);
          break;
        }
        case getType(FavoriteActions.request): {
          const linkedUserName = action.payload && action.payload.userName;
          loadFavorites(nextState, dispatch, linkedUserName);
          break;
        }
      }
      return result;
    } catch (err) {
      console.error('DashboardMiddleware :: Caught an exception for action ', action, err);
    }
  };
};
